import { createContext, useContext, useReducer } from 'react';
import { isFunction } from 'lodash';

import { useCurrentUser } from 'components/root/Container/CurrentUserContainer';

import { createSessionStorageCacheProvider } from './filterCacheStorage';
import reducer from './reducer';

const StateContext = createContext(null);
const DispatchContext = createContext(null);

export function EuiFiltersProvider({ children, cachePrefix, version, createCacheProvider }) {
  const { roleBasedId } = useCurrentUser();
  const cacheKey = `${cachePrefix}-${roleBasedId}`;

  const [state, dispatch] = useReducer(
    reducer,
    {
      cacheKey,
      version,
      createCacheProvider
    },
    init
  );

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </StateContext.Provider>
  );
}

export function withEuiFiltersProvider(
  Component,
  { cachePrefix, version = '0.1', createCacheProvider = createSessionStorageCacheProvider }
) {
  return function WithEuiFiltersProvider(props) {
    return (
      <EuiFiltersProvider cachePrefix={cachePrefix} version={version} createCacheProvider={createCacheProvider}>
        <Component {...props} />
      </EuiFiltersProvider>
    );
  };
}

export function useEuiFiltersState() {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('useEuiFiltersState must be used within a EuiFiltersContext');
  }
  return context;
}

export function useEuiFiltersDispatch() {
  const dispatch = useContext(DispatchContext);

  if (dispatch === undefined) {
    throw new Error('useEuiFiltersDispatch must be used within a EuiFiltersContext');
  }

  return dispatch;
}

function init(initialState) {
  const { cacheKey, createCacheProvider } = initialState;
  const nextState = { ...initialState };
  if (isFunction(createCacheProvider)) {
    nextState.cacheProvider = createCacheProvider(cacheKey);
  }
  return nextState;
}
